// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import DataTable from "@insite/mobius/DataTable";
import { DataTableBody, DataTableCell, DataTableHead, DataTableHeader, DataTableRow, Link } from "@insite/mobius/index";
import translate from "@insite/client-framework/Translate";
import { tableProps } from "@store/Themes/common/props/table";
import MobiusAccordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import ManagedAccordionSection from "@store/MobiusOverrides/AccordionSection/ManagedAccordionSection";
import { AccordionSectionPanel, AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import { accordionProductDetailsSectionProps } from "@store/Themes/common/props/accordionProductDetails";
import getColor from "@insite/mobius/utilities/getColor";

const enum fields {
    numberOfItems = "numberOfItems",
}

export interface ProductDetailsPartsListProps extends WidgetProps {
    fields: {
        [fields.numberOfItems]: number;
    };
}

type Props = ProductDetailsPartsListProps & HasProduct;

export interface ProductsInKit {
    ProductDescription: string;
    ProductId: string;
    ProductSku: string;
    ProductUrl: string;
    Quantity: number;
}

export interface ProductDetailsPartsListStyles {
    seeMoreLinkWrapper?: InjectableCss;
    tableWrapper?: InjectableCss;
    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const productDetailsPartsListStyles: ProductDetailsPartsListStyles = {
    seeMoreLinkWrapper: {
        css: css`
            margin-top: ${pxToRem(15)};
        `,
    },
    tableWrapper: tableProps.wrapper,
    accordion: {
        css: css`
            margin-top: ${pxToRem(30)};
        `,
    },
    accordionSection: {
        headerProps: {
            ...accordionProductDetailsSectionProps.headerProps,
            css: css`
                ${accordionProductDetailsSectionProps.headerProps?.css}

                ${AccordionSectionPanel} + && {
                    margin-top: ${pxToRem(15)};
                }

                && button[aria-expanded="true"] {
                    border-color: ${getColor("common.border")};
                }
            `,
        },
        panelProps: {
            ...accordionProductDetailsSectionProps.panelProps,
            css: css`
                ${accordionProductDetailsSectionProps.panelProps?.css}
            `,
        },
        mergeCss: true,
    },
};

const styles = productDetailsPartsListStyles;

const ProductDetailsPartsList = ({ product, fields: { numberOfItems } }: Props) => {
    const [expanded, setExpanded] = React.useState(false);

    const partsList: ProductsInKit[] = product.properties.productsInKit
        ? JSON.parse(product.properties.productsInKit)
        : null;

    if (!partsList || partsList.length === 0) {
        return null;
    }

    const limitedPartsList = expanded ? partsList : partsList.slice(0, numberOfItems);

    return (
        <MobiusAccordion headingLevel={1} {...styles.accordion}>
            <ManagedAccordionSection
                initialExpanded={true}
                title={translate("Configuration")}
                {...styles.accordionSection}
            >
                <StyledWrapper>
                    <StyledWrapper {...styles.tableWrapper}>
                        <DataTable>
                            <DataTableHead>
                                <DataTableHeader>{translate("Part Number")}</DataTableHeader>
                                <DataTableHeader>{translate("Description")}</DataTableHeader>
                                <DataTableHeader>{translate("Quantity")}</DataTableHeader>
                            </DataTableHead>
                            <DataTableBody>
                                {limitedPartsList.map(part => (
                                    <DataTableRow key={part.ProductId}>
                                        <DataTableCell>
                                            {part.ProductUrl ? (
                                                <Link href={part.ProductUrl}>{part.ProductSku}</Link>
                                            ) : (
                                                <>{part.ProductSku}</>
                                            )}
                                        </DataTableCell>
                                        <DataTableCell>{part.ProductDescription}</DataTableCell>
                                        <DataTableCell>{part.Quantity}</DataTableCell>
                                    </DataTableRow>
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </StyledWrapper>
                    {partsList.length > numberOfItems && (
                        <StyledWrapper {...styles.seeMoreLinkWrapper}>
                            {expanded ? (
                                <Link onClick={() => setExpanded(false)}>{translate("See Less")}</Link>
                            ) : (
                                <Link onClick={() => setExpanded(true)}>{translate("See All")}</Link>
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </ManagedAccordionSection>
        </MobiusAccordion>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(ProductDetailsPartsList),
    definition: {
        displayName: "Product Parts List",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.numberOfItems,
                displayName: "Number of items displayed",
                editorTemplate: "IntegerField",
                fieldType: "General",
                defaultValue: 10,
            },
        ],
    },
};

export default widgetModule;
